import { create } from 'zustand';

import type { MapMode } from '@utils/googleMapOptions';

// ─────────────────────────────────────────────────────────────────────────────
// Global map preferences store
// - `mode` defaults to the user's OS theme on first load, then sticks in
//   localStorage once they explicitly toggle the switch.
// - `spotlight` defaults to `false` and is persisted once toggled.
// Set-once resolution — matchMedia is read only during initial bootstrap,
// never subscribed to after that.
// ─────────────────────────────────────────────────────────────────────────────

const MODE_KEY = 'map-preferences-mode';
const SPOTLIGHT_KEY = 'map-preferences-spotlight';

type MapPreferencesStore = {
  mode: MapMode;
  spotlight: boolean;
};

const readInitialMode = (): MapMode => {
  try {
    const stored = localStorage.getItem(MODE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    /* localStorage unavailable */
  }
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'dark';
};

const readInitialSpotlight = (): boolean => {
  try {
    const stored = localStorage.getItem(SPOTLIGHT_KEY);
    if (stored === 'true') return true;
    if (stored === 'false') return false;
  } catch {
    /* localStorage unavailable */
  }
  return false;
};

export const useMapPreferencesStore = create<MapPreferencesStore>(() => ({
  mode: readInitialMode(),
  spotlight: readInitialSpotlight(),
}));

export const setMapMode = (mode: MapMode) => {
  useMapPreferencesStore.setState({ mode });
  try {
    localStorage.setItem(MODE_KEY, mode);
  } catch {
    /* noop */
  }
};

export const setMapSpotlight = (spotlight: boolean) => {
  useMapPreferencesStore.setState({ spotlight });
  try {
    localStorage.setItem(SPOTLIGHT_KEY, spotlight ? 'true' : 'false');
  } catch {
    /* noop */
  }
};
